import { FileUploadContext } from '@/context/file-upload-context'
import { AuthContext } from '@/context/auth-context'
import { PaperclipAttachment } from '@/assets/icons'
import * as Popover from '@radix-ui/react-popover'
import { useContext, useState, useEffect, useRef, type ReactElement, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import AssistantDocumentSelectionDialog from '../../source-dialogs/SelectionDialogAssistant'
import { Folder, Upload, ChevronRight } from '@/assets/icons'
import { selectAgentV2NextTurnWritingSampleFileId } from '@/agent-v2/chat-form/store/selectors'
import { selectAgentV2ConversationMatterId } from '@/agent-v2/conversations/store/selectors'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent, TemplateSource } from '@/analytics/schema/events.schema'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { kPaxtonName } from '@/constants/constants-strings'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { IconButton } from '@/components/icon-button/IconButton'
import type { IconButtonSize } from '@/components/icon-button/types'
import { matterPaths } from '@/routing/paths'
import { ADD_FILES_MODAL_TAB, type AddFilesModalInitialTab } from '../../source-dialogs/AddFilesModal'
import { useExternalStorageMenuStatus, type ExternalStorageMenuStatus } from '@/user_files/external-storage/hooks/useExternalStorageMenuStatus'
import { FilePickerSecurityFooter } from '@/user_files/external-storage/components/FilePickerSecurityFooter'

type FileActionMenuProps = {
  disabled?: boolean
  conversationId: string
  /** Trigger icon override. Defaults to the paperclip. */
  icon?: ReactElement
  /** Trigger button size override. Defaults to the "lg" size. */
  size?: Exclude<IconButtonSize, 'xs'>
  /** Popover alignment relative to the trigger. Defaults to "center". */
  align?: 'start' | 'center' | 'end'
  /** Embedded hosts can open matter settings outside the current router. */
  onOpenMatterSettings?: (matterId: string) => void
  onOpenGlobalSettings?: () => void
}

enum FileAction {
  Upload = 'upload',
  Select = 'select',
}

enum TabType {
  AddFiles = 'add-files',
  ApplyTemplate = 'apply-template',
}

function getMenuItemClassName(disabled: boolean, hasDescription: boolean) {
  return `flex w-full items-center gap-2.5 rounded-md text-left ${hasDescription ? 'min-h-10 px-2 py-0' : 'p-2'} ${
    disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-brand-neutral-100'
  }`
}

function getTabClassName(active: boolean) {
  return `relative flex h-7 w-[158px] items-center justify-center px-4 py-1.5 text-[12px] font-semibold uppercase focus:outline-hidden ${
    active
      ? 'text-brand-neutral-950 after:absolute after:bottom-[-1px] after:left-0 after:h-[3px] after:w-full after:bg-brand-neutral-950'
      : 'text-brand-neutral-500'
  }`
}

const fileActionUpload = {
  value: FileAction.Upload,
  label: 'Upload files',
  description: 'Select documents from your computer',
  icon: <Upload />,
}

const fileActionSelect = {
  value: FileAction.Select,
  label: 'Add from matter files',
  description: 'Select from matter files',
  icon: <Folder />,
}

type FileActionMenuItemProps = {
  icon: ReactNode
  label: string
  description?: string
  onClick: () => void
  ariaLabel?: string
  disabled?: boolean
  title?: string
  trailingIcon?: ReactNode
}

function FileActionMenuItem({ icon, label, description, onClick, ariaLabel, disabled = false, title, trailingIcon }: FileActionMenuItemProps) {
  return (
    <button
      className={getMenuItemClassName(disabled, !!description)}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      role="menuitem"
      disabled={disabled}
      title={title}
    >
      <div className="shrink-0 text-brand-neutral-700 [&_svg]:h-4 [&_svg]:w-4">{icon}</div>
      {description ? (
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0">
          <span className="text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-700">{label}</span>
          <span className="truncate text-xs font-normal leading-4 text-brand-neutral-600">{description}</span>
        </div>
      ) : (
        <span className="min-w-0 flex-1 text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-700">{label}</span>
      )}
      {trailingIcon && <div className="shrink-0 text-brand-neutral-700 [&_svg]:h-4 [&_svg]:w-4">{trailingIcon}</div>}
    </button>
  )
}

function ExternalStorageFileActionMenuItem({
  onConnectFolder,
  onOpenExternalFiles,
  status,
}: {
  onConnectFolder: () => void
  onOpenExternalFiles: () => void
  status: ExternalStorageMenuStatus
}) {
  if (!status.showMenuItem || !status.connectedProviderMeta || !status.ConnectedProviderIcon) {
    return null
  }
  const ConnectedProviderIcon = status.ConnectedProviderIcon

  if (status.actionKind === 'open-files') {
    return <FileActionMenuItem icon={<ConnectedProviderIcon />} label={`Add from ${status.connectedProviderMeta.label}`} onClick={onOpenExternalFiles} />
  }

  return (
    <FileActionMenuItem
      icon={<ConnectedProviderIcon />}
      label={`Connect a ${status.connectedProviderMeta.label} folder to this matter`}
      description="Attach synced files anywhere in this matter"
      onClick={onConnectFolder}
      trailingIcon={<ChevronRight />}
    />
  )
}

export default function FileActionMenu(props: FileActionMenuProps) {
  const { disabled, conversationId, icon = <PaperclipAttachment />, size = 'lg', align = 'center', onOpenMatterSettings } = props
  const artifactTemplatesEnabled = kGetFeatureFlagStatus(kFeatureFlags.ARTIFACT_TEMPLATES)
  const { openSystemUserFilesFileSelector, openSystemWritingSampleFileSelector, userFilesUploadDisabled, userFilesUploadDisabledReason } =
    useContext(FileUploadContext)
  const addFilesActions = [{ ...fileActionUpload, disabled: userFilesUploadDisabled, title: userFilesUploadDisabledReason }, fileActionSelect]
  const [open, setPopoverOpen] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<TabType>(TabType.AddFiles)
  const [docSourceDialogIsOpen, setDocSourceDialogOpen] = useState<boolean>(false)
  const [initialAddFilesTab, setInitialAddFilesTab] = useState<AddFilesModalInitialTab>(ADD_FILES_MODAL_TAB.MATTER_FILES)
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const { userAccountData } = useContext(AuthContext)
  const organizationId = getDriveSyncOrganizationId(userAccountData)
  const prevTabRef = useRef<TabType>(activeTab)

  const conversationMatterId = useAppSelector((state: RootState) => selectAgentV2ConversationMatterId(state, { conversationId }))
  const activeMatterId = useAppSelector(selectActiveMatterId)
  const effectiveMatterId = conversationMatterId ?? activeMatterId
  const externalStorageAction = useExternalStorageMenuStatus({
    matterId: effectiveMatterId,
    menuOpen: open,
  })

  const hasWritingSample = useAppSelector((state: RootState) => selectAgentV2NextTurnWritingSampleFileId(state, { conversationId }) !== null)

  // Local helper: is the Apply Template tab active?
  const isTemplateContext = activeTab === TabType.ApplyTemplate

  // Analytics: track when the user opens/closes the Apply Template tab
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      if (isTemplateContext) {
        trackEvent(AnalyticsEvent.ApplyTemplateTabOpened, { conversationId })
      } else if (prevTabRef.current === TabType.ApplyTemplate && activeTab === TabType.AddFiles) {
        trackEvent(AnalyticsEvent.ApplyTemplateTabClosed, { conversationId })
      }
    }
    prevTabRef.current = activeTab
  }, [activeTab, isTemplateContext, conversationId, trackEvent])

  const handleSelection = (action: FileAction) => {
    setPopoverOpen(false)

    if (action === FileAction.Upload) {
      if (isTemplateContext) {
        trackEvent(AnalyticsEvent.TemplateSourceChosen, {
          conversationId,
          templateSourceType: TemplateSource.upload,
          isFirstTemplate: !hasWritingSample,
        })
        openSystemWritingSampleFileSelector()
      } else {
        openSystemUserFilesFileSelector()
      }
    } else if (action === FileAction.Select) {
      if (isTemplateContext) {
        trackEvent(AnalyticsEvent.TemplateSourceChosen, {
          conversationId,
          templateSourceType: TemplateSource.drive,
          isFirstTemplate: !hasWritingSample,
        })
      }
      setInitialAddFilesTab(ADD_FILES_MODAL_TAB.MATTER_FILES)
      setDocSourceDialogOpen(true)
    }
  }

  const handleExternalFilesSelection = () => {
    setPopoverOpen(false)
    setInitialAddFilesTab(ADD_FILES_MODAL_TAB.EXTERNAL_FILES)
    setDocSourceDialogOpen(true)
  }

  const handleConnectFolder = () => {
    setPopoverOpen(false)
    if (effectiveMatterId) {
      // This menu lives under the chat route, which would otherwise tag the event with
      // feature: 'agent'. trackMatterDriveSyncEvent's explicit feature + suppressFeatureInheritance
      // wins over route inheritance in useAnalytics, so Drive Sync events are always tagged
      // 'matter_drive_sync' regardless of call-site route.
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncSetupEntryClicked,
        {
          matterId: effectiveMatterId,
          organizationId,
          matterDriveSyncEntrySource: MatterDriveSyncEntrySource.FileActionMenu,
          ...(externalStorageAction.displayProvider ? { externalStorageProvider: externalStorageAction.displayProvider } : {}),
        },
        trackEvent
      )
      if (onOpenMatterSettings) onOpenMatterSettings(effectiveMatterId)
      else navigate(matterPaths.settings(effectiveMatterId))
    }
  }

  // Handler for when a file is selected from the dialog
  const handleDialogClose = (value: boolean) => {
    setDocSourceDialogOpen(value)
  }

  return (
    <div className="flex">
      <Popover.Root open={open} onOpenChange={setPopoverOpen}>
        <Popover.Trigger asChild>
          <IconButton
            icon={icon}
            variant="plain"
            color="neutral"
            size={size}
            onClick={() => setPopoverOpen(!open)}
            aria-label="Add files by uploading or selecting from file manager"
            title="Add Files"
            type="button"
            disabled={disabled}
          />
        </Popover.Trigger>
        {!externalStorageAction.loading && (
          <Popover.Portal>
            <Popover.Content
              align={align}
              onEscapeKeyDown={() => setPopoverOpen(false)}
              onInteractOutside={() => setPopoverOpen(false)}
              className={`PopoverContent z-50 w-[348px] ${align === 'center' ? 'mr-5' : ''} bg-white rounded-lg shadow-menu overflow-hidden`}
            >
              <div className="flex flex-col">
                {/* Tab navigation to switch between Add Files and Writing Sample */}
                <div className="flex justify-center border-b border-brand-neutral-200 px-4 pt-1.5">
                  <button
                    className={getTabClassName(activeTab === TabType.AddFiles)}
                    onClick={() => {
                      setActiveTab(TabType.AddFiles)
                    }}
                  >
                    ADD FILES
                  </button>

                  <button
                    className={getTabClassName(isTemplateContext)}
                    onClick={() => {
                      setActiveTab(TabType.ApplyTemplate)
                    }}
                  >
                    {/* Today this feature is called "Apply Template". The new artifact templates feature (apply a template
                      at download) takes over that name when it ships, so this one is renamed to "Writing Sample" then. */}
                    {artifactTemplatesEnabled ? 'WRITING SAMPLE' : 'APPLY TEMPLATE'}
                  </button>
                </div>

                {/* Content based on active tab */}
                {activeTab === TabType.AddFiles && (
                  <div className="flex flex-col gap-2 p-2">
                    {addFilesActions.map((action) => (
                      <FileActionMenuItem
                        key={action.value}
                        icon={action.icon}
                        label={action.label}
                        onClick={() => handleSelection(action.value)}
                        disabled={'disabled' in action ? action.disabled : false}
                        title={'title' in action ? action.title : undefined}
                      />
                    ))}
                    <ExternalStorageFileActionMenuItem
                      status={externalStorageAction}
                      onOpenExternalFiles={handleExternalFilesSelection}
                      onConnectFolder={handleConnectFolder}
                    />
                  </div>
                )}

                {isTemplateContext && (
                  <>
                    {!hasWritingSample && (
                      <div className="px-4 pt-4">
                        <div className="w-full rounded-lg border border-brand-neutral-200 bg-brand-neutral-50 px-3 py-3">
                          <p className="text-[12px] font-medium leading-[16px] text-brand-neutral-600">
                            Select a document for {kPaxtonName} to follow in style, tone, and reasoning.
                            {artifactTemplatesEnabled && (
                              <span className="font-sans font-semibold text-xs leading-4 text-brand-800 mt-2"> Formatting is applied at download.</span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col gap-2 p-2">
                      <FileActionMenuItem
                        icon={<Upload />}
                        label="Upload file"
                        onClick={() => handleSelection(FileAction.Upload)}
                        disabled={userFilesUploadDisabled}
                        title={userFilesUploadDisabledReason}
                      />
                      <FileActionMenuItem icon={<Folder />} label="Add from matter files" onClick={() => handleSelection(FileAction.Select)} />
                      <ExternalStorageFileActionMenuItem
                        status={externalStorageAction}
                        onOpenExternalFiles={handleExternalFilesSelection}
                        onConnectFolder={handleConnectFolder}
                      />
                    </div>
                  </>
                )}

                <FilePickerSecurityFooter
                  variant={externalStorageAction.footerVariant}
                  onNavigate={() => setPopoverOpen(false)}
                  onOpenSettings={props.onOpenGlobalSettings}
                />
              </div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </Popover.Root>
      <AssistantDocumentSelectionDialog
        open={docSourceDialogIsOpen}
        conversationId={conversationId}
        onClose={handleDialogClose}
        isTemplateMode={isTemplateContext}
        initialAddFilesTab={initialAddFilesTab}
      />
    </div>
  )
}
